import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Download, ExternalLink, Instagram, Loader2, Music2, X, Youtube } from "lucide-react";
import { api, API, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const LS_KEY = "prodrushi_free_unlocks";

const readUnlocks = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}");
  } catch {
    return {};
  }
};

export const getFreeUnlockToken = (beatId) => readUnlocks()[beatId] || null;

const saveUnlock = (beatId, token) => {
  const all = readUnlocks();
  all[beatId] = token;
  localStorage.setItem(LS_KEY, JSON.stringify(all));
};

const ACTIONS = [
  { id: "youtube", label: "Subscribe on YouTube", handle: "@PRODRUSHI", url: "https://youtube.com/@prodrushi", Icon: Youtube },
  { id: "instagram", label: "Follow on Instagram", handle: "@prod.rushi", url: "https://www.instagram.com/prod.rushi/", Icon: Instagram },
  { id: "spotify", label: "Follow on Spotify", handle: "RUSHI", url: "https://open.spotify.com/artist/1fVSxNbesvw20EEMIUc4OU", Icon: Music2 },
  { id: "soundcloud", label: "Follow on SoundCloud", handle: "producedbyrushi", url: "https://soundcloud.com/producedbyrushi", Icon: Music2 },
];

export const FreeUnlockModal = ({ beat, onClose }) => {
  const [done, setDone] = useState([]);
  const [busy, setBusy] = useState(false);
  const [unlockedToken, setUnlockedToken] = useState(null);

  useEffect(() => {
    if (beat) {
      setDone([]);
      setUnlockedToken(getFreeUnlockToken(beat.id));
    }
  }, [beat]);

  const markDone = (id) => {
    window.open(ACTIONS.find((a) => a.id === id)?.url, "_blank", "noopener");
    setDone((d) => (d.includes(id) ? d : [...d, id]));
  };

  const unlock = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/beats/free-unlock", { beat_id: beat.id, actions: done });
      saveUnlock(beat.id, data.download_token);
      setUnlockedToken(data.download_token);
      toast.success("Free download unlocked");
    } catch {
      toast.error("Could not unlock — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {beat && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/80 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          data-testid="free-unlock-modal"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-white/10 bg-[#141419] sm:rounded-3xl"
          >
            <div className="flex items-center justify-between border-b border-white/10 p-5 sm:p-6">
              <div className="flex items-center gap-4">
                <img src={fileUrl(beat.cover_url)} alt={beat.title} className="h-14 w-14 rounded-xl object-cover" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">Free download</p>
                  <h3 className="font-heading text-xl font-bold text-white" data-testid="free-unlock-beat-title">{beat.title}</h3>
                </div>
              </div>
              <button data-testid="free-unlock-close" onClick={onClose}
                className="rounded-full border border-white/10 p-2 text-zinc-400 transition-colors hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 sm:p-6">
              {unlockedToken ? (
                <div className="text-center" data-testid="free-unlock-ready">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-300/15">
                    <Check size={26} className="text-emerald-300" />
                  </div>
                  <p className="font-heading mt-4 text-lg font-bold text-white">Unlocked</p>
                  <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                    Tagged MP3 for non-profit use (demos, writing, freestyles). Dropping the song commercially?
                    Grab a lease to clear it.
                  </p>
                  <a
                    href={`${API}/download/free/${unlockedToken}`}
                    data-testid="free-download-button"
                    className="mt-6 flex items-center justify-center gap-2 rounded-full bg-emerald-300 py-3.5 text-xs font-bold uppercase tracking-wider text-black transition-colors hover:bg-emerald-200"
                  >
                    <Download size={14} /> Download MP3
                  </a>
                </div>
              ) : (
                <>
                  <p className="text-sm text-zinc-400">
                    This one is free — tap a platform below, hit follow or subscribe, then unlock your download.
                  </p>
                  <div className="mt-5 space-y-2.5" data-testid="free-unlock-actions">
                    {ACTIONS.map(({ id, label, handle, Icon }) => {
                      const isDone = done.includes(id);
                      return (
                        <button
                          key={id}
                          onClick={() => markDone(id)}
                          data-testid={`free-action-${id}`}
                          className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                            isDone
                              ? "border-emerald-300/50 bg-emerald-300/10"
                              : "border-white/10 bg-[#0B0B0E] hover:border-emerald-300/40"
                          }`}
                        >
                          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${isDone ? "bg-emerald-300 text-black" : "bg-white/5 text-zinc-300"}`}>
                            {isDone ? <Check size={16} /> : <Icon size={16} />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-bold text-white">{label}</span>
                            <span className="block truncate font-mono text-[11px] text-zinc-500">{handle}</span>
                          </span>
                          <ExternalLink size={14} className="shrink-0 text-zinc-600" />
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={unlock}
                    disabled={done.length === 0 || busy}
                    data-testid="free-unlock-confirm-button"
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-300 py-3.5 text-xs font-bold uppercase tracking-wider text-black transition-colors hover:bg-emerald-200 disabled:opacity-40"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    Unlock Free Download
                  </button>
                  <p className="mt-3 text-center text-[11px] text-zinc-600">
                    Non-profit use only — buy a lease to release commercially.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
